import type { Database, Json } from "@mistribazaar/supabase/src/database.types";

type AddressInsert = Database["public"]["Tables"]["addresses"]["Insert"];
type OrderInsert = Database["public"]["Tables"]["orders"]["Insert"];
type OrderItemInsert = Database["public"]["Tables"]["order_items"]["Insert"];
type ServiceBookingInsert = Database["public"]["Tables"]["service_bookings"]["Insert"];
type MaterialOrderInsert = Database["public"]["Tables"]["material_orders"]["Insert"];
type OrderUpdate = Database["public"]["Tables"]["orders"]["Update"];

type CheckoutProfile = {
  id?: string | null;
  full_name?: string | null;
  name?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  house_flat_no?: string | null;
  flat_house_no?: string | null;
  street_area?: string | null;
};

type AddressForm = {
  houseFlatNo: string;
  streetArea: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
};

type ResolvedAddress = AddressForm;

type CheckoutMembership = {
  name: string;
  duration: string;
};

type CheckoutSummary = {
  serviceSubtotal: number;
  materialSubtotal: number;
  serviceFee: number;
  visitationFee: number;
  platformFee: number;
  deliveryCharge: number;
  tax: number;
  total: number;
  membershipPrice: number;
};

type CartItem = {
  type: string;
  title: string;
  description?: string;
  quantity?: number | string;
  unit?: string | null;
  basePrice?: number | string;
  finalPrice?: number | string;
  materialId?: string | null;
  serviceId?: string | null;
  shopName?: string | null;
  imageUrl?: string | null;
  serviceCategory?: string | null;
  duration?: string | null;
  shopId?: string | null;
  distanceKm?: number | string;
  vehicleType?: string | null;
  requiresProjectBrief?: boolean;
  bookingMode?: string | null;
};

type ProjectBrief = {
  budgetRange: string;
  urgency: string;
  plotSize: string;
  projectDetails: string;
  attachmentUrls: string;
};

export function buildTypedAddressPayload({
  profile,
  addressForm,
  selectedAddressType,
  userId,
}: {
  profile: CheckoutProfile | null | undefined;
  addressForm: AddressForm;
  selectedAddressType: string;
  userId: string;
}): AddressInsert {
  const useProfile = selectedAddressType === "profile";

  return {
    user_id: userId,
    full_name: profile?.full_name || "Customer",
    phone: profile?.phone || "",
    house_flat_no: useProfile
      ? profile?.house_flat_no || addressForm.houseFlatNo
      : addressForm.houseFlatNo,
    street_area: useProfile
      ? profile?.street_area || addressForm.streetArea
      : addressForm.streetArea,
    city: useProfile ? profile?.city || addressForm.city : addressForm.city,
    state: useProfile ? profile?.state || addressForm.state : addressForm.state,
    country: useProfile
      ? profile?.country || addressForm.country || "India"
      : addressForm.country || "India",
    pincode: useProfile ? profile?.pincode || addressForm.pincode : addressForm.pincode,
    label: useProfile ? "Profile Address" : "Order Address",
    is_default: false,
  };
}

export function buildTypedOrderPayload({
  userId,
  orderCode,
  orderType,
  paymentStatus,
  paymentMethod,
  membership,
  summary,
  addressId,
  notes,
}: {
  userId: string;
  orderCode: string;
  orderType: string;
  paymentStatus: string;
  paymentMethod: string;
  membership: CheckoutMembership | null;
  summary: CheckoutSummary;
  addressId: string;
  notes: string;
}): OrderInsert {
  return {
    user_id: userId,
    order_code: orderCode,
    order_type: orderType,
    status: "order_placed",
    payment_status: paymentStatus,
    payment_method: paymentMethod,
    membership_name: membership ? membership.name : null,
    service_subtotal: summary.serviceSubtotal,
    material_subtotal: summary.materialSubtotal,
    service_fee: summary.serviceFee + summary.visitationFee,
    platform_fee: summary.platformFee,
    delivery_charge: summary.deliveryCharge,
    tax: summary.tax,
    membership_discount: 0,
    total_amount: summary.total,
    address_id: addressId,
    notes,
  };
}

export function buildTypedOrderItemsPayload({
  cartItems,
  orderId,
  transportResponsibility,
  visitationFee,
  membership,
  membershipPrice,
}: {
  cartItems: CartItem[];
  orderId: string;
  transportResponsibility: string;
  visitationFee: number;
  membership: CheckoutMembership | null;
  membershipPrice: number;
}): OrderItemInsert[] {
  const payload: OrderItemInsert[] = cartItems.map((item) => ({
    order_id: orderId,
    item_type: item.type,
    ref_id: item.materialId || item.serviceId || null,
    title: item.title,
    description: item.description || "",
    quantity: Number(item.quantity || 1),
    unit: item.unit || null,
    unit_price: Number(item.basePrice || item.finalPrice || 0),
    final_price: Number(item.finalPrice || 0),
    metadata: {
      warranty: null,
      shopName: item.shopName || null,
      imageUrl: item.imageUrl || null,
      serviceCategory: item.serviceCategory || null,
      duration: item.duration || null,
      transportResponsibility: item.type === "service" ? transportResponsibility : null,
    } satisfies Json,
  }));

  if (visitationFee > 0) {
    payload.push({
      order_id: orderId,
      item_type: "fee",
      ref_id: null,
      title: "Mandatory Visitation Fee",
      description: "Applied to service items priced below Rs300 at Rs99 per qualifying service quantity.",
      quantity: 1,
      unit: "charge",
      unit_price: Number(visitationFee || 0),
      final_price: Number(visitationFee || 0),
      metadata: {
        fee_type: "visitation",
      } satisfies Json,
    });
  }

  if (membership) {
    payload.push({
      order_id: orderId,
      item_type: "membership",
      ref_id: null,
      title: `Membership Plan - ${membership.name}`,
      description: `${membership.duration} plan with 10% cashback on eligible completed orders.`,
      quantity: 1,
      unit: "plan",
      unit_price: Number(membershipPrice || 0),
      final_price: Number(membershipPrice || 0),
      metadata: {
        membership_name: membership.name,
        cashback_rate: "10%",
      } satisfies Json,
    });
  }

  return payload;
}

export function buildTypedServiceBookingsPayload({
  items,
  orderId,
  userId,
  resolvedAddress,
  resolvedAddressText,
  transportResponsibility,
  selectedDate,
  selectedSlot,
  profile,
  projectBrief,
}: {
  items: CartItem[];
  orderId: string;
  userId: string;
  resolvedAddress: ResolvedAddress;
  resolvedAddressText: string;
  transportResponsibility: string;
  selectedDate: string;
  selectedSlot: string;
  profile: CheckoutProfile | null | undefined;
  projectBrief: ProjectBrief;
}): ServiceBookingInsert[] {
  return items.map((item) => ({
    order_id: orderId,
    user_id: userId,
    service_title: item.title,
    service_category: item.serviceCategory || "general",
    service_slug:
      (item.title || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "") || null,
    booking_mode: item.bookingMode || null,
    booking_date: selectedDate,
    booking_slot: selectedSlot,
    city: resolvedAddress.city || null,
    address_text: resolvedAddressText || null,
    transport_responsibility: transportResponsibility || null,
    customer_name: profile?.full_name || profile?.name || null,
    customer_phone: profile?.phone || null,
    budget_range: projectBrief.budgetRange || null,
    urgency: projectBrief.urgency || null,
    plot_size: projectBrief.plotSize || null,
    project_details: projectBrief.projectDetails || null,
    attachment_urls: projectBrief.attachmentUrls
      ? projectBrief.attachmentUrls
          .split(",")
          .map((itemValue) => itemValue.trim())
          .filter(Boolean)
      : [],
    status: "pending_assignment",
    rate: Number(item.finalPrice || 0),
    notes: `${item.description || ""}${item.description ? " " : ""}Transport responsibility: ${transportResponsibility}.`.trim(),
  }));
}

export function buildTypedMaterialOrdersPayload({
  items,
  orderId,
  userId,
  resolvedAddress,
  resolvedAddressText,
  priorityDelivery,
  getMaterialDeliveryEstimate,
}: {
  items: CartItem[];
  orderId: string;
  userId: string;
  resolvedAddress: ResolvedAddress;
  resolvedAddressText: string;
  priorityDelivery: boolean;
  getMaterialDeliveryEstimate: (args: {
    quantity: number | string;
    priorityDelivery: boolean;
    distanceKm: number | string;
    vehicleType: string;
  }) => string;
}): MaterialOrderInsert[] {
  return items.map((item) => ({
    order_id: orderId,
    user_id: userId,
    material_title: item.title,
    shop_id: item.shopId || null,
    delivery_city: resolvedAddress.city || null,
    delivery_address_text: resolvedAddressText || null,
    quantity: Number(item.quantity || 1),
    unit: item.unit || "unit",
    unit_price: Number(item.basePrice || item.finalPrice || 0),
    final_price: Number(item.finalPrice || 0),
    priority_delivery: priorityDelivery,
    delivery_estimate: getMaterialDeliveryEstimate({
      quantity: item.quantity || 1,
      priorityDelivery,
      distanceKm: item.distanceKm || 5,
      vehicleType: item.vehicleType || "bike",
    }),
    delivery_status: "awaiting_shop_confirmation",
    assigned_delivery_partner: null,
    tracking_id: null,
  }));
}

export function buildTypedPaidOrderUpdate(): OrderUpdate {
  return {
    payment_status: "paid",
    payment_method: "online",
  };
}
